/**
 * Poll backend /health/ready for startup and reconnect flows.
 */

import { getBackendBaseUrl } from '@/services/config/env';

const DEFAULT_TIMEOUT_MS = 120_000;
const POLL_INTERVAL_MS = 500;

/**
 * @param {{ timeoutMs?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<string>} Resolved backend base URL when ready.
 */
export async function waitForBackendReady(options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const base = getBackendBaseUrl();
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (options.signal?.aborted) {
      throw new Error('Backend readiness check aborted');
    }
    try {
      const res = await fetch(`${base}/health/ready`, {
        method: 'GET',
        cache: 'no-store',
        signal: options.signal,
      });
      if (res.ok) {
        return base;
      }
    } catch {
      // Backend still starting
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }

  throw new Error(`Backend not ready after ${timeoutMs}ms (${base})`);
}

/**
 * @returns {Promise<boolean>}
 */
export async function isBackendAvailable() {
  try {
    const res = await fetch(`${getBackendBaseUrl()}/health`, {
      method: 'GET',
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    return false;
  }
}
